import React, { useState, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from '../hooks/redux'
import { addBooking, updateBooking, type Booking } from '../store/slices/bookingsSlice'
import { addGuest } from '../store/slices/guestsSlice'
import { getSeasonForDate } from '../store/slices/roomsSlice'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { getApiUrl } from '../utils/api'

interface BookingFormData {
  id: string
  guestName: string
  guestEmail: string
  guestPhone: string
  unitType: string
  unitNumber: string
  checkIn: string
  checkOut: string
  guests: string
  totalAmount: number
  status: string
  payment: string
  seasonApplied?: string
  specialRequests?: string
}

interface PricingBreakdown {
  baseRate: number
  nights: number
  subtotal: number
  adminFee: number
  breakageDeposit: number
  total: number
}

interface RoomUnitOption {
  id: number
  roomNumber: string
  displayRoomNumber?: string
  roomTypeId: number
  unitType: string
  status: string
}

type BackendBookingRecord = {
  id?: string | number
  booking_reference?: string
  guest_name?: string
  guest_email?: string
  guest_phone?: string
  unit_type?: string
  room_number?: string | number
  room_id?: string | number
  check_in?: string
  check_out?: string
  nights?: string | number
  guests_count?: string | number
  base_rate?: string | number
  admin_fee?: string | number
  breakage_deposit?: string | number
  total_amount?: string | number
  season_applied?: string
  status?: string
  payment_status?: string
}

const CANONICAL_UNIT_TYPES = [
  '4-Sleeper Unit',
  '6-Sleeper Unit',
  'Bigger 6-Sleeper Unit',
] as const

const canonicalizeUnitType = (value: string): string | null => {
  const normalized = String(value || '').trim().toLowerCase()

  if (!normalized) {
    return null
  }

  if (normalized.includes('bigger')) {
    return 'Bigger 6-Sleeper Unit'
  }

  if (normalized.includes('4')) {
    return '4-Sleeper Unit'
  }

  if (normalized.includes('6')) {
    return '6-Sleeper Unit'
  }

  return null
}

const mapBackendBookingToBooking = (row: BackendBookingRecord): Booking => ({
  id: String(row.booking_reference ?? row.id ?? ''),
  guestName: String(row.guest_name ?? ''),
  guestEmail: String(row.guest_email ?? ''),
  guestPhone: String(row.guest_phone ?? ''),
  unitType: canonicalizeUnitType(String(row.unit_type ?? '')) || String(row.unit_type ?? ''),
  unitNumber: String(row.room_number ?? row.room_id ?? ''),
  checkIn: String(row.check_in ?? '').split('T')[0],
  checkOut: String(row.check_out ?? '').split('T')[0],
  guests: String(row.guests_count ?? ''),
  totalAmount: Number(row.total_amount ?? 0),
  status: row.status
    ? String(row.status).charAt(0).toUpperCase() + String(row.status).slice(1)
    : 'Pending',
  payment: String(row.payment_status ?? 'pending'),
  nights: Number(row.nights ?? 0),
  baseRate: Number(row.base_rate ?? 0),
  adminFee: Number(row.admin_fee ?? 0),
  breakageDeposit: Number(row.breakage_deposit ?? 0),
  seasonApplied: row.season_applied ? String(row.season_applied) : undefined,
})

const getJohannesburgDateString = (date = new Date()) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Johannesburg',
}).format(date)

const getTomorrowDateString = () => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return getJohannesburgDateString(tomorrow)
}

const addDaysToDateString = (value: string, days: number) => {
  if (!value) {
    return ''
  }

  const nextDate = new Date(`${value}T00:00:00`)
  nextDate.setDate(nextDate.getDate() + days)
  return getJohannesburgDateString(nextDate)
}

const BookingPreviewPageNew: React.FC = () => {
  const router = useCompatRouter()
  const dispatch = useAppDispatch()
  
  const bookingIdParam = router.query.id
  const bookingTokenParam = router.query.token
  const previewLoadingParam = router.query.previewLoading
  const modeParam = router.query.mode
  const bookingId = Array.isArray(bookingIdParam) ? bookingIdParam[0] : bookingIdParam
  const bookingToken = Array.isArray(bookingTokenParam) ? bookingTokenParam[0] : bookingTokenParam
  const previewLoadingValue = Array.isArray(previewLoadingParam) ? previewLoadingParam[0] : previewLoadingParam
  const isPreviewLoading = previewLoadingValue === '1' || previewLoadingValue === 'true'
  const mode = (Array.isArray(modeParam) ? modeParam[0] : modeParam) || 'view' // 'view', 'edit', 'create'
  
  const { bookings } = useAppSelector((state) => state.bookings)
  const { units, seasonalCalendar, globalSeasons } = useAppSelector((state) => state.rooms)
  const authToken = useAppSelector((state) => state.auth.token)
  
  const [isEditing, setIsEditing] = useState(mode === 'edit' || mode === 'create')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const [formData, setFormData] = useState<BookingFormData>({
    id: '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    unitType: '',
    unitNumber: '',
    checkIn: '',
    checkOut: '',
    guests: '1',
    totalAmount: 0,
    status: 'Confirmed',
    payment: 'Pending',
    seasonApplied: '',
    specialRequests: ''
  })
  
  const [breakdown, setBreakdown] = useState<PricingBreakdown>({
    baseRate: 0,
    nights: 0,
    subtotal: 0,
    adminFee: 0,
    breakageDeposit: 0,
    total: 0
  })
  const [roomUnits, setRoomUnits] = useState<RoomUnitOption[]>([])
  const createDraftBookingIdRef = useRef<string>('')

  const formatUnitTypeLabel = (unitType: string) => {
    return canonicalizeUnitType(unitType) || unitType
  }

  const applyStoredPricing = (booking: Booking) => {
    const baseRate = Number(booking.baseRate ?? 0)
    const adminFee = Number(booking.adminFee ?? 0)
    const breakageDeposit = Number(booking.breakageDeposit ?? 0)
    const derivedNights = Math.max(
      1,
      Math.ceil((new Date(booking.checkOut).getTime() - new Date(booking.checkIn).getTime()) / (1000 * 3600 * 24))
    )
    const nights = Number.isFinite(Number(booking.nights)) && Number(booking.nights) > 0
      ? Number(booking.nights)
      : derivedNights

    if (!Number.isFinite(baseRate) || baseRate <= 0) {
      return false
    }

    const subtotal = baseRate * nights
    setBreakdown({
      baseRate,
      nights,
      subtotal,
      adminFee: Number.isFinite(adminFee) ? adminFee : 0,
      breakageDeposit: Number.isFinite(breakageDeposit) ? breakageDeposit : 0,
      total: Number.isFinite(Number(booking.totalAmount)) && Number(booking.totalAmount) > 0
        ? Number(booking.totalAmount)
        : subtotal + (Number.isFinite(adminFee) ? adminFee : 0) + (Number.isFinite(breakageDeposit) ? breakageDeposit : 0),
    })

    return true
  }

  const resolveSelectedUnit = (unitType: string, unitNumber?: string) => {
    if (!unitType) {
      return undefined
    }

    if (unitNumber?.trim()) {
      const exactUnit = units.find((unit) => unit.number === unitNumber && canonicalizeUnitType(unit.type) === canonicalizeUnitType(unitType))
      if (exactUnit) {
        return exactUnit
      }
    }

    return units.find((unit) => canonicalizeUnitType(unit.type) === canonicalizeUnitType(unitType))
  }

  const resolveSelectedRoomUnit = (unitType: string, unitNumber?: string) => {
    if (!unitType || !unitNumber?.trim()) {
      return undefined
    }

    return roomUnits.find((roomUnit) => roomUnit.unitType === unitType && roomUnit.roomNumber === unitNumber)
  }

  useEffect(() => {
    let cancelled = false

    const loadRoomUnits = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/rooms/units`)
        const result = await response.json()

        if (!response.ok || !result?.success || !Array.isArray(result.data) || cancelled) {
          return
        }

        const mappedRoomUnits: RoomUnitOption[] = result.data.map((item: Record<string, unknown>) => ({
          id: Number(item.id ?? 0),
          roomNumber: String(item.roomNumber ?? ''),
          displayRoomNumber: item.displayRoomNumber ? String(item.displayRoomNumber) : String(item.roomNumber ?? ''),
          roomTypeId: Number(item.roomTypeId ?? 0),
          unitType: canonicalizeUnitType(String(item.unitType ?? '')) || String(item.unitType ?? ''),
          status: String(item.status ?? 'available'),
        }))

        setRoomUnits(mappedRoomUnits)
      } catch (error) {
        console.error('Failed to load room units:', error)
      }
    }

    void loadRoomUnits()

    return () => {
      cancelled = true
    }
  }, [])

  // Calculate pricing breakdown
  const calculatePricing = (
    checkIn: string,
    checkOut: string,
    unitType: string,
    unitNumber?: string,
    adminFee?: number,
    breakageDeposit?: number
  ) => {
    if (!checkIn || !checkOut || !unitType) return

    const checkInDate = new Date(checkIn)
    const checkOutDate = new Date(checkOut)
    const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 3600 * 24))
    
    if (nights <= 0) return

    const selectedUnit = resolveSelectedUnit(unitType, unitNumber)
    
    // Get the season for the check-in date to determine the rate
    const season = getSeasonForDate(checkIn, unitType, seasonalCalendar, globalSeasons)
    let baseRate = selectedUnit?.midSeasonRate || 150
    
    // Apply seasonal rate
    switch (season) {
      case 'high':
        baseRate = selectedUnit?.highSeasonRate || baseRate
        break
      case 'low':
        baseRate = selectedUnit?.lowSeasonRate || baseRate
        break
      default: // 'mid'
        baseRate = selectedUnit?.midSeasonRate || baseRate
        break
    }
    
    const currentAdminFee = adminFee !== undefined ? adminFee : (breakdown.adminFee || selectedUnit?.adminFee || 50)
    const currentBreakageDeposit = breakageDeposit !== undefined ? breakageDeposit : (breakdown.breakageDeposit || selectedUnit?.breakageDeposit || 100)
    
    const subtotal = baseRate * nights
    const total = subtotal + currentAdminFee + currentBreakageDeposit

    const newBreakdown = {
      baseRate,
      nights,
      subtotal,
      adminFee: currentAdminFee,
      breakageDeposit: currentBreakageDeposit,
      total
    }

    setBreakdown(newBreakdown)
    setFormData(prev => ({ ...prev, totalAmount: total, seasonApplied: season }))
  }

  // Handle admin fee change
  const handleAdminFeeChange = (newAdminFee: number) => {
    const updatedTotal = breakdown.subtotal + newAdminFee + breakdown.breakageDeposit
    setBreakdown(prev => ({
      ...prev,
      adminFee: newAdminFee,
      total: updatedTotal
    }))
    setFormData(prev => ({ ...prev, totalAmount: updatedTotal }))
  }

  // Handle breakage deposit change
  const handleBreakageDepositChange = (newBreakageDeposit: number) => {
    const updatedTotal = breakdown.subtotal + breakdown.adminFee + newBreakageDeposit
    setBreakdown(prev => ({
      ...prev,
      breakageDeposit: newBreakageDeposit,
      total: updatedTotal
    }))
    setFormData(prev => ({ ...prev, totalAmount: updatedTotal }))
  }

  // Generate new booking ID
  const generateBookingId = () => {
    const lastId = bookings.length > 0 
      ? Math.max(...bookings.map(b => parseInt(b.id.replace('B', '')) || 0))
      : 0
    return `B${String(lastId + 1).padStart(4, '0')}`
  }

  useEffect(() => {
    if (!router.isReady || mode !== 'create' || createDraftBookingIdRef.current) {
      return
    }

    createDraftBookingIdRef.current = generateBookingId()
  }, [router.isReady, mode, bookings])

  useEffect(() => {
    const loadBookingData = async () => {
      if (!router.isReady) {
        return
      }

      if (mode === 'create') {
        const draftBookingId = createDraftBookingIdRef.current || generateBookingId()
        createDraftBookingIdRef.current = draftBookingId

        // Initialize for new booking once and preserve the visible reference across rerenders.
        setFormData(prev => ({
          ...prev,
          id: prev.id || draftBookingId,
          checkIn: prev.checkIn || getTomorrowDateString(),
          checkOut: prev.checkOut || addDaysToDateString(getTomorrowDateString(), 1)
        }))
        setLoading(false)
        return
      }

      if (!bookingId && !bookingToken) {
        if (isPreviewLoading) {
          return
        }

        setLoading(false)
        return
      }

      // Load existing booking for view/edit mode
      let foundBooking = bookings.find(b => b.id === bookingId)
      
      if (!foundBooking) {
        try {
          // Try to load from localStorage with multiple possible keys
          const possibleKeys = ['resort_bookings', 'marinaglen-bookings', 'bookings']
          for (const key of possibleKeys) {
            const storedBookings = localStorage.getItem(key)
            if (storedBookings) {
              const parsedBookings = JSON.parse(storedBookings)
              foundBooking = parsedBookings.find((b: any) => b.id === bookingId)
              if (foundBooking) {
                console.log(`Found booking in localStorage with key: ${key}`)
                break
              }
            }
          }
          
          // Try to load from a temporary booking storage (for new bookings)
          if (!foundBooking) {
            const tempBookingData = localStorage.getItem(`temp_booking_${bookingId}`)
            if (tempBookingData) {
              try {
                const parsed = JSON.parse(tempBookingData)
                // Check if the temp data has the new structure with expiration
                if (parsed.booking && parsed.expires) {
                  if (Date.now() < parsed.expires) {
                    foundBooking = parsed.booking
                    console.log('Found booking in temporary storage (new format)')
                  } else {
                    // Remove expired temp booking
                    localStorage.removeItem(`temp_booking_${bookingId}`)
                    console.log('Removed expired temporary booking')
                  }
                } else {
                  // Old format - just the booking data
                  foundBooking = parsed
                  console.log('Found booking in temporary storage (old format)')
                }
              } catch (error) {
                console.error('Error parsing temporary booking data:', error)
                localStorage.removeItem(`temp_booking_${bookingId}`)
              }
            }
          }
        } catch (error) {
          console.error('Failed to load booking from storage:', error)
        }
      }

      if (!foundBooking && bookingToken) {
        try {
          const response = await fetch(`${getApiUrl()}/api/bookings/public/${encodeURIComponent(bookingToken)}`)
          const result = await response.json()

          if (response.ok && result?.success && result?.data) {
            foundBooking = mapBackendBookingToBooking(result.data as BackendBookingRecord)
          }
        } catch (error) {
          console.error('Failed to load booking from public API:', error)
        }
      }

      if (!foundBooking && bookingId && authToken) {
        try {
          const response = await fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(bookingId)}`, {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          })
          const result = await response.json()

          if (response.ok && result?.success && result?.data) {
            foundBooking = mapBackendBookingToBooking(result.data as BackendBookingRecord)
          }
        } catch (error) {
          console.error('Failed to load booking from admin API:', error)
        }
      }

      if (foundBooking) {
        setFormData({
          id: foundBooking.id,
          guestName: foundBooking.guestName,
          guestEmail: foundBooking.guestEmail,
          guestPhone: foundBooking.guestPhone,
          unitType: foundBooking.unitType,
          unitNumber: foundBooking.unitNumber,
          checkIn: foundBooking.checkIn,
          checkOut: foundBooking.checkOut,
          guests: foundBooking.guests,
          totalAmount: foundBooking.totalAmount,
          status: foundBooking.status,
          payment: foundBooking.payment,
          seasonApplied: foundBooking.seasonApplied || '',
          specialRequests: ''
        })
        if (!applyStoredPricing(foundBooking)) {
          calculatePricing(foundBooking.checkIn, foundBooking.checkOut, foundBooking.unitType, foundBooking.unitNumber)
        }
      }
      setLoading(false)
    }

    loadBookingData()
  }, [router.isReady, bookingId, bookingToken, isPreviewLoading, mode, bookings, units, authToken])

  // Handle form input changes
  const handleInputChange = (field: keyof BookingFormData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'unitType' ? { unitNumber: '' } : {}),
    }))
    
    // Recalculate pricing when relevant fields change
    if (field === 'checkIn' || field === 'checkOut' || field === 'unitType' || field === 'unitNumber') {
      const newData = {
        ...formData,
        [field]: value,
        ...(field === 'unitType' ? { unitNumber: '' } : {}),
      }
      calculatePricing(
        newData.checkIn,
        newData.checkOut,
        newData.unitType,
        newData.unitNumber,
        breakdown.adminFee,
        breakdown.breakageDeposit
      )
    }
    
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const displayedSeason = (formData.seasonApplied || '').toLowerCase() || (
    formData.checkIn && formData.unitType
      ? getSeasonForDate(
          formData.checkIn,
          resolveSelectedUnit(formData.unitType, formData.unitNumber)?.type || formData.unitType,
          seasonalCalendar,
          globalSeasons
        )
      : ''
  )

  const getSelectedUnitMinimumStay = () => {
    const selectedUnit = resolveSelectedUnit(formData.unitType, formData.unitNumber)
    return Math.max(1, Number(selectedUnit?.minStay ?? 1))
  }

  const earliestReservationDeskCheckIn = getTomorrowDateString()
  const earliestReservationDeskCheckOut = formData.checkIn && formData.checkIn >= earliestReservationDeskCheckIn
    ? addDaysToDateString(formData.checkIn, 1)
    : addDaysToDateString(earliestReservationDeskCheckIn, 1)

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.guestName.trim()) newErrors.guestName = 'Guest name is required'
    if (!formData.guestEmail.trim()) newErrors.guestEmail = 'Email is required'
    if (!/\S+@\S+\.\S+/.test(formData.guestEmail)) newErrors.guestEmail = 'Invalid email format'
    if (!formData.guestPhone.trim()) newErrors.guestPhone = 'Phone number is required'
    if (!formData.unitType) newErrors.unitType = 'Unit type is required'
    if (!formData.unitNumber.trim()) newErrors.unitNumber = 'Unit number is required'
    if (!formData.checkIn) newErrors.checkIn = 'Check-in date is required'
    if (!formData.checkOut) newErrors.checkOut = 'Check-out date is required'
    if (formData.checkIn && formData.checkIn < earliestReservationDeskCheckIn) {
      newErrors.checkIn = 'Reservation desk bookings must have an arrival date from tomorrow onward'
    }
    if (new Date(formData.checkIn) >= new Date(formData.checkOut)) {
      newErrors.checkOut = 'Check-out must be after check-in'
    }
    const requestedNights = Math.max(
      0,
      Math.ceil((new Date(formData.checkOut).getTime() - new Date(formData.checkIn).getTime()) / (1000 * 3600 * 24))
    )
    const minimumStay = getSelectedUnitMinimumStay()
    if (formData.checkIn && formData.checkOut && requestedNights < minimumStay) {
      newErrors.checkOut = `This unit requires a minimum stay of ${minimumStay} night${minimumStay === 1 ? '' : 's'}`
    }
    if (!formData.guests || parseInt(formData.guests) < 1) {
      newErrors.guests = 'Number of guests must be at least 1'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Save booking
  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    try {
      const bookingData: Booking = {
        id: formData.id,
        guestName: formData.guestName,
        guestEmail: formData.guestEmail,
        guestPhone: formData.guestPhone,
        unitType: formData.unitType,
        unitNumber: formData.unitNumber,
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        guests: formData.guests,
        totalAmount: formData.totalAmount,
        status: formData.status,
        payment: formData.payment,
        nights: breakdown.nights,
        baseRate: breakdown.baseRate,
        adminFee: breakdown.adminFee,
        breakageDeposit: breakdown.breakageDeposit,
        seasonApplied: getSeasonForDate(
          formData.checkIn,
          resolveSelectedUnit(formData.unitType, formData.unitNumber)?.type || formData.unitType,
          seasonalCalendar,
          globalSeasons
        )
      }

      if (mode === 'create') {
        // Persist booking to backend database
        const selectedUnit = resolveSelectedUnit(formData.unitType, formData.unitNumber)
        const selectedRoomUnit = resolveSelectedRoomUnit(formData.unitType, formData.unitNumber)
        const season = getSeasonForDate(formData.checkIn, selectedUnit?.type || formData.unitType, seasonalCalendar, globalSeasons)
        const nights = breakdown.nights > 0
          ? breakdown.nights
          : Math.max(0, Math.ceil(
              (new Date(formData.checkOut).getTime() - new Date(formData.checkIn).getTime()) / (1000 * 3600 * 24)
            ))

        const apiRes = await fetch(`${getApiUrl()}/api/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_reference: formData.id,
            room_id: selectedRoomUnit?.id,
            room_type_id: selectedRoomUnit?.roomTypeId ?? (selectedUnit ? parseInt(selectedUnit.id) : undefined),
            check_in: formData.checkIn,
            check_out: formData.checkOut,
            nights,
            guests_count: parseInt(formData.guests),
            base_rate: breakdown.baseRate || 0,
            admin_fee: breakdown.adminFee || 0,
            breakage_deposit: breakdown.breakageDeposit || 0,
            total_amount: formData.totalAmount,
            status: formData.status.toLowerCase(),
            payment_status: formData.payment.toLowerCase(),
            special_requests: formData.specialRequests || null,
            season_applied: season,
            guest_name: formData.guestName,
            guest_email: formData.guestEmail,
            guest_phone: formData.guestPhone,
            booking_source: 'reservation_desk',
          }),
        })

        const apiJson = await apiRes.json().catch(() => ({}))

        if (!apiRes.ok) {
          throw new Error((apiJson as { error?: string }).error || `Server error ${apiRes.status}`)
        }

        const persistedBooking = apiJson && typeof apiJson === 'object' && 'data' in apiJson && (apiJson as { data?: BackendBookingRecord }).data
          ? mapBackendBookingToBooking((apiJson as { data: BackendBookingRecord }).data)
          : bookingData

        dispatch(addBooking(persistedBooking))

        // Save to temporary storage for booking-preview window access
        const tempData = {
          booking: persistedBooking,
          timestamp: Date.now(),
          expires: Date.now() + (60 * 60 * 1000)
        }
        localStorage.setItem(`temp_booking_${persistedBooking.id}`, JSON.stringify(tempData))

        // Add guest to Redux for immediate Guest Management display
        dispatch(addGuest({
          id: `G${Date.now()}`,
          name: formData.guestName,
          email: formData.guestEmail,
          phone: formData.guestPhone,
          totalBookings: 1,
          totalSpent: formData.totalAmount,
          lastVisit: formData.checkIn,
          status: 'Active' as const,
          createdAt: new Date().toISOString()
        }))

        const guestEmailStatus = (apiJson as { data?: { emailDelivery?: { guest?: string, warnings?: string[] } }, warning?: string }).data?.emailDelivery?.guest || null
        const emailWarning = (apiJson as { warning?: string }).warning || null
        const guestEmailMessage = emailWarning
          ? ` Email warning: ${emailWarning}`
          : guestEmailStatus === 'sent'
            ? ` Guest confirmation sent to ${formData.guestEmail}.`
            : ''

        alert(`Booking created successfully!${guestEmailMessage}`)
        await router.push('/booking-system/admin')
        return
      } else {
        // Update status/payment in backend
        await fetch(`${getApiUrl()}/api/bookings/${encodeURIComponent(formData.id)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            status: formData.status.toLowerCase(),
            payment_status: formData.payment.toLowerCase(),
          }),
        }).catch((err) => console.error('PATCH booking error:', err))

        dispatch(updateBooking(bookingData))

        const tempData = {
          booking: bookingData,
          timestamp: Date.now(),
          expires: Date.now() + (60 * 60 * 1000)
        }
        localStorage.setItem(`temp_booking_${bookingData.id}`, JSON.stringify(tempData))
      }

      alert(`Booking ${mode === 'create' ? 'created' : 'updated'} successfully!`)
      await router.push('/booking-system/admin')
    } catch (error) {
      console.error('Error saving booking:', error)
      alert(`Failed to save booking: ${error instanceof Error ? error.message : 'Please try again.'}`)
    } finally {
      setSaving(false)
    }
  }

  const printBooking = () => {
    window.print()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    )
  }

  if (mode !== 'create' && !formData.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h1>
          <p className="text-gray-600 mb-4">
            {bookingId ? `Booking ID "${bookingId}" could not be found.` : bookingToken ? 'The booking preview link is invalid or has expired.' : 'No booking ID provided.'}
          </p>
          <div className="text-sm text-gray-500 mb-4 bg-gray-100 p-3 rounded">
            <p><strong>Debug Info:</strong></p>
            <p>Mode: {mode}</p>
            <p>Booking ID: {bookingId || 'None'}</p>
            <p>Redux Bookings: {bookings.length}</p>
            <p>localStorage Keys: {typeof window !== 'undefined' ? (Object.keys(localStorage).filter(k => k.includes('booking')).join(', ') || 'None') : 'Unavailable during SSR'}</p>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            This may happen if the booking was recently deleted or if there's a data synchronization issue.
          </p>
          <div className="space-x-2">
            <button 
              onClick={() => void router.push('/booking-system/admin')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Admin Dashboard
            </button>
            <button 
              onClick={() => void router.push('/booking-system/booking-form?mode=create')}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Create New Booking
            </button>
          </div>
        </div>
      </div>
    )
  }

  const availableUnitTypes = [...CANONICAL_UNIT_TYPES]

  const availableUnitNumbers = roomUnits
    .filter((roomUnit) => roomUnit.unitType === formData.unitType)
    .sort((left, right) => left.roomNumber.localeCompare(right.roomNumber))

  return (
    <div className="booking-form-theme min-h-screen bg-gray-50 print:bg-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b print:shadow-none print:border-b-2 print:border-gray-300">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {mode === 'create' ? 'Create New Booking' : 
                 mode === 'edit' ? 'Edit Booking' : 'Booking Details'}
              </h1>
              <p className="text-gray-600 mt-1">Marina Glen Holiday Resort</p>
            </div>
            <div className="flex space-x-3 print:hidden">
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={printBooking}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    🖨️ Print
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? '💾 Saving...' : '💾 Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setErrors({})
                    }}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    ❌ Cancel
                  </button>
                </>
              )}
              <button
                onClick={() => void router.push('/booking-system/admin')}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Main Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Booking Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Booking ID</label>
                  <input
                    type="text"
                    value={formData.id}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  {isEditing ? (
                    <select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Checked In">Checked In</option>
                      <option value="Checked Out">Checked Out</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  ) : (
                    <div className={`px-3 py-2 rounded-md border font-medium ${
                      formData.status === 'Confirmed' ? 'bg-green-50 border-green-200 text-green-800' :
                      formData.status === 'Pending' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                      formData.status === 'Checked In' ? 'bg-blue-50 border-blue-200 text-blue-800' :
                      formData.status === 'Checked Out' ? 'bg-purple-50 border-purple-200 text-purple-800' :
                      'bg-red-50 border-red-200 text-red-800'
                    }`}>
                      {formData.status}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Guest Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Guest Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name *</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.guestName}
                      onChange={(e) => handleInputChange('guestName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.guestName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter guest name"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.guestName}
                    </div>
                  )}
                  {errors.guestName && <p className="text-red-500 text-sm mt-1">{errors.guestName}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests *</label>
                  {isEditing ? (
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.guests}
                      onChange={(e) => handleInputChange('guests', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.guests ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.guests}
                    </div>
                  )}
                  {errors.guests && <p className="text-red-500 text-sm mt-1">{errors.guests}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.guestEmail}
                      onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.guestEmail ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="guest@email.com"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.guestEmail}
                    </div>
                  )}
                  {errors.guestEmail && <p className="text-red-500 text-sm mt-1">{errors.guestEmail}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.guestPhone}
                      onChange={(e) => handleInputChange('guestPhone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.guestPhone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="+1-555-0123"
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.guestPhone}
                    </div>
                  )}
                  {errors.guestPhone && <p className="text-red-500 text-sm mt-1">{errors.guestPhone}</p>}
                </div>
              </div>
            </div>

            {/* Accommodation Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Accommodation Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type *</label>
                  {isEditing ? (
                    <select
                      value={formData.unitType}
                      onChange={(e) => handleInputChange('unitType', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.unitType ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select unit type</option>
                      {availableUnitTypes.map(type => (
                        <option key={type} value={type}>{formatUnitTypeLabel(type)}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.unitType}
                    </div>
                  )}
                  {errors.unitType && <p className="text-red-500 text-sm mt-1">{errors.unitType}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit Number *</label>
                  {isEditing ? (
                    <select
                      value={formData.unitNumber}
                      onChange={(e) => handleInputChange('unitNumber', e.target.value)}
                      disabled={!formData.unitType}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.unitNumber ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">{formData.unitType ? 'Select unit number' : 'Select unit type first'}</option>
                      {availableUnitNumbers.map((roomUnit) => (
                        <option key={roomUnit.id} value={roomUnit.roomNumber}>{roomUnit.displayRoomNumber || roomUnit.roomNumber}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.unitNumber}
                    </div>
                  )}
                  {errors.unitNumber && <p className="text-red-500 text-sm mt-1">{errors.unitNumber}</p>}
                </div>
              </div>
            </div>

            {/* Stay Dates */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Stay Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date *</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={formData.checkIn}
                      onChange={(e) => handleInputChange('checkIn', e.target.value)}
                      min={earliestReservationDeskCheckIn}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.checkIn ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.checkIn ? new Date(formData.checkIn).toLocaleDateString() : 'Not set'}
                    </div>
                  )}
                  {errors.checkIn && <p className="text-red-500 text-sm mt-1">{errors.checkIn}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date *</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={formData.checkOut}
                      onChange={(e) => handleInputChange('checkOut', e.target.value)}
                      min={earliestReservationDeskCheckOut}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.checkOut ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                      {formData.checkOut ? new Date(formData.checkOut).toLocaleDateString() : 'Not set'}
                    </div>
                  )}
                  {errors.checkOut && <p className="text-red-500 text-sm mt-1">{errors.checkOut}</p>}
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-700">
                  <strong>Arrival rule:</strong> Reservation desk bookings must start from tomorrow onward.
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Length of Stay:</strong> {breakdown.nights} night{breakdown.nights !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {/* Special Requests */}
            {isEditing && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Special Requests</h3>
                <textarea
                  value={formData.specialRequests || ''}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any special requests or notes..."
                />
              </div>
            )}
          </div>

          {/* Right Column - Pricing and Summary */}
          <div className="space-y-6">
            
            {/* Pricing Breakdown */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Breakdown</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Rate (per night)</span>
                  <span className="font-medium">R {breakdown.baseRate.toLocaleString()}</span>
                </div>
                
                {/* Season Indicator */}
                {displayedSeason && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-gray-600">Season Applied</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      displayedSeason === 'high' 
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : displayedSeason === 'low'
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      {displayedSeason.toUpperCase()} SEASON
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">Number of Nights</span>
                  <span className="font-medium">{breakdown.nights}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">R {breakdown.subtotal.toLocaleString()}</span>
                </div>
                
                {/* Admin Fee - Editable */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Admin Fee</span>
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">R</span>
                      <input
                        type="number"
                        value={breakdown.adminFee}
                        onChange={(e) => handleAdminFeeChange(Number(e.target.value) || 0)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                        min="0"
                      />
                    </div>
                  ) : (
                    <span className="font-medium">R {breakdown.adminFee.toLocaleString()}</span>
                  )}
                </div>

                {/* Breakage Deposit - Editable */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Breakage Deposit</span>
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">R</span>
                      <input
                        type="number"
                        value={breakdown.breakageDeposit}
                        onChange={(e) => handleBreakageDepositChange(Number(e.target.value) || 0)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                        min="0"
                      />
                    </div>
                  ) : (
                    <span className="font-medium">R {breakdown.breakageDeposit.toLocaleString()}</span>
                  )}
                </div>
                
                <hr className="my-3" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount</span>
                  <span className="text-blue-600">R {breakdown.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Payment Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                {isEditing ? (
                  <select
                    value={formData.payment}
                    onChange={(e) => handleInputChange('payment', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Partial">Partial</option>
                    <option value="Refunded">Refunded</option>
                  </select>
                ) : (
                  <div className={`px-3 py-2 rounded-md border font-medium ${
                    formData.payment === 'Paid' ? 'bg-green-50 border-green-200 text-green-800' :
                    formData.payment === 'Partial' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                    formData.payment === 'Refunded' ? 'bg-purple-50 border-purple-200 text-purple-800' :
                    'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    {formData.payment}
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Summary</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Guest:</strong> {formData.guestName || 'Not specified'}</div>
                <div><strong>Unit:</strong> {formData.unitNumber} ({formData.unitType})</div>
                <div><strong>Dates:</strong> {formData.checkIn ? new Date(formData.checkIn).toLocaleDateString() : 'Not set'} - {formData.checkOut ? new Date(formData.checkOut).toLocaleDateString() : 'Not set'}</div>
                <div><strong>Duration:</strong> {breakdown.nights} night{breakdown.nights !== 1 ? 's' : ''}</div>
                <div><strong>Total:</strong> R {breakdown.total.toLocaleString()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingPreviewPageNew